import type { HttpTypes } from "@medusajs/types";

import type { CheckoutAddressFormData } from "./validations";

/**
 * A shipping or billing address in the shape the cart update takes.
 *
 * Every field lands as a string - a saved address carries nulls for the
 * optional ones and the form carries `undefined`, and both are normalized to
 * `""` here so the two sources cannot produce subtly different payloads for
 * the same address.
 */
export interface ResolvedCheckoutAddress {
  first_name: string;
  last_name: string;
  address_1: string;
  address_2: string;
  company: string;
  city: string;
  country_code: string;
  province: string;
  postal_code: string;
  phone: string;
}

/**
 * Anything the checkout can call an address: a saved `StoreCustomerAddress`
 * (nulls for the optional fields), an inline one off the form (`undefined`),
 * or an already-resolved one.
 */
export type CheckoutAddressSource = {
  [K in keyof ResolvedCheckoutAddress]?: string | null;
};

/**
 * Normalize one address into the shape the cart update takes.
 *
 * `phoneOverride` is the contact number the buyer typed for this order. It
 * wins where it is passed, which is shipping only - see {@link resolveBilling}
 * for why billing does not take one.
 *
 * Exported because the address *change* handlers write a cart address too,
 * outside a submit: {@link useShippingAddressChange} pushes the new address
 * straight at the carts so shipping options and postcode pricing recalculate.
 * They used to hand-roll these ten lines each, and the shipping one omitted the
 * phone rule below - so selecting an address without a saved phone wrote `""`
 * to the cart, and the submit that followed wrote the real number. Same address,
 * two answers, depending on which of the two paths ran last.
 */
export function toCheckoutAddress(
  address: CheckoutAddressSource,
  phoneOverride?: string
): ResolvedCheckoutAddress {
  return {
    first_name: address.first_name || "",
    last_name: address.last_name || "",
    address_1: address.address_1 || "",
    address_2: address.address_2 || "",
    company: address.company || "",
    city: address.city || "",
    country_code: address.country_code || "",
    province: address.province || "",
    postal_code: address.postal_code || "",
    phone: phoneOverride || address.phone || ""
  };
}

export type ResolveAddressesResult =
  | {
      ok: true;
      shipping: ResolvedCheckoutAddress;
      billing: ResolvedCheckoutAddress;
    }
  | { ok: false; error: string };

/**
 * Turn the checkout form's three ways of naming an address into the two
 * addresses the carts actually need.
 *
 * The buyer can supply each of shipping and billing as a saved address id or as
 * an inline one-time address, and billing additionally as "same as shipping" -
 * so the same resolution runs twice against slightly different inputs. That is
 * the whole reason this is a module: the branching is identical enough to look
 * like duplication and different enough that keeping the copies in step by hand
 * is what goes wrong.
 *
 * Both checkout steps submit through here - {@link ShippingStep} to write the
 * addresses onto the carts, {@link PaymentStep} to pay against them. They each
 * carried their own copy of these eighty-odd lines, and the two steps of a
 * single checkout disagreeing about what one form meant is the failure this
 * exists to make impossible.
 *
 * Pure, and it **returns** the failure rather than reporting it. Both steps
 * previously interleaved this with `setSubmitError(...)` + `return` at four
 * points each, which meant the branching could only be exercised by rendering a
 * whole step - and in practice never was: the payment step's tests stub the
 * address hook out entirely, and the shipping step has no tests at all. A
 * returned result is the same information without the coupling.
 *
 * @param data - the submitted checkout form
 * @param addresses - the customer's saved addresses, to resolve an id against
 */
export function resolveCheckoutAddresses(
  data: CheckoutAddressFormData,
  addresses: HttpTypes.StoreCustomerAddress[]
): ResolveAddressesResult {
  const shipping = resolveShipping(data, addresses);
  if ("error" in shipping) {
    return { ok: false, error: shipping.error };
  }

  const billing = resolveBilling(data, addresses, shipping.address);
  if ("error" in billing) {
    return { ok: false, error: billing.error };
  }

  return { ok: true, shipping: shipping.address, billing: billing.address };
}

type Resolved = { address: ResolvedCheckoutAddress } | { error: string };

function resolveShipping(
  data: CheckoutAddressFormData,
  addresses: HttpTypes.StoreCustomerAddress[]
): Resolved {
  if (data.shippingAddressId) {
    const saved = addresses.find((a) => a.id === data.shippingAddressId);
    if (!saved) {
      return { error: "Please select a shipping address" };
    }
    // The form's phone wins over the saved one: it is the number the buyer
    // typed for *this* order.
    return { address: toCheckoutAddress(saved, data.phone) };
  }

  if (data.shippingAddress) {
    return { address: toCheckoutAddress(data.shippingAddress, data.phone) };
  }

  return { error: "Please select or enter a shipping address" };
}

function resolveBilling(
  data: CheckoutAddressFormData,
  addresses: HttpTypes.StoreCustomerAddress[],
  shipping: ResolvedCheckoutAddress
): Resolved {
  if (data.sameAsShipping) {
    return { address: { ...shipping } };
  }

  if (data.billingAddressId) {
    const saved = addresses.find((a) => a.id === data.billingAddressId);
    if (!saved) {
      return { error: "Please select a billing address" };
    }
    // No `data.phone` override on billing - that field is the delivery contact
    // number, and applying it to a billing address the buyer did not attach it
    // to would silently rewrite their saved record's phone on the order.
    return { address: toCheckoutAddress(saved) };
  }

  if (data.billingAddress) {
    return { address: toCheckoutAddress(data.billingAddress) };
  }

  return { error: "Please select or enter a billing address" };
}
